import { useAuth } from '@hourly/contexts/AuthContext';
import { type Currency } from '@hourly/utilities/Currency';
import { Button, Chip, ChipVariant, type Column, SearchInput, Table } from '@hourly-hds/components';
import { Price } from '@hourly-hds/icons/colored';
import { StarLine } from '@hourly-hds/icons/glyph';
import { useState } from 'react';
import Spinner from '@shared/components/Spinner';
import Page from '../../_layout/components/Page';
import InfoBlock from '../_index/components/InfoBlock';
import SplitView from '../_index/components/SplitView/SplitView';
import { useRevenueShareExport } from './data/hooks/useRevenueShareExport';
import { useRevenueShareLedger } from './data/hooks/useRevenueShareLedger';
import {
  type Percentage,
  type RevenueShare,
  type RevenueSharePeriod,
  RevenueShareStatus,
} from './data/models/revenueShare.model';
import styles from './RevenueShare.view.module.css';

const StatusChip = ({ status }: { status: RevenueShareStatus }) => {
  const statusChipColor = {
    [RevenueShareStatus.open]: ChipVariant.Success,
    [RevenueShareStatus.submitted]: ChipVariant.Information,
    [RevenueShareStatus.paid]: undefined,
  };
  return <Chip variant={statusChipColor[status]}>{status}</Chip>;
};

const columns: Column<RevenueShare>[] = [
  {
    header: 'Account',
    accessorKey: 'businessName',
    enableSorting: true,
  },
  {
    header: 'Fees',
    accessorKey: 'fee',
    align: 'right',
    cell: (info: { getValue: () => Currency }) => info.getValue().toString(),
    enableSorting: true,
  },
  {
    header: 'Revenue Share %',
    accessorKey: 'percentage',
    align: 'right',
    cell: (info: { getValue: () => Percentage }) => info.getValue().toString(),
    enableSorting: true,
  },
  {
    header: 'Revenue Share',
    accessorKey: 'total',
    align: 'right',
    cell: (info: { getValue: () => Currency }) => info.getValue().toString(),
    enableSorting: true,
  },
] as Column<RevenueShare>[];

function RevenueShareView() {
  const { sessionState } = useAuth();
  const { fetchExportFilename } = useRevenueShareExport();
  const { revenueShareLedger, isSettled } = useRevenueShareLedger(sessionState.partnerId!);
  const [currentPeriod, setCurrentPeriod] = useState<RevenueSharePeriod | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isSettled) {
    return <Spinner />;
  }

  const handleChangeSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };

  const selectedPeriod = currentPeriod || revenueShareLedger.latestPeriod()!;
  const revenueShares = selectedPeriod?.filterBy(searchTerm) || [];
  return (
    <Page>
      <Page.Header>
        <Page.Title>Revenue Share</Page.Title>
      </Page.Header>
      <Page.Content hasPadding={false}>
        <div className={styles.container}>
          {!revenueShareLedger.hasPeriods() && (
            <div className={styles.emptyState}>
              <StarLine />
              <p>No Data</p>
            </div>
          )}
          {revenueShareLedger.hasPeriods() && (
            <SplitView>
              <SplitView.Sidebar>
                {revenueShareLedger.periods().map((period: RevenueSharePeriod) => (
                  <SplitView.Item key={period.id} isSelected={selectedPeriod.id === period.id}>
                    <div
                      className={styles.itemContainer}
                      onClick={() => setCurrentPeriod(period)}
                      aria-label={`Revenue share period ${period.date}, status ${period.status}`}>
                      <div>
                        <div className={styles.itemTitle}>{period.date}</div>
                        <StatusChip status={period.status} />
                      </div>
                      <div>{period.total().toString()}</div>
                    </div>
                  </SplitView.Item>
                ))}
              </SplitView.Sidebar>
              <SplitView.Content>
                <div className={styles.header}>
                  <div className={styles.headerInfo}>
                    <Price />
                    <InfoBlock label="Payment Date">{selectedPeriod.paymentDate}</InfoBlock>
                    <InfoBlock label="Period">{selectedPeriod.date}</InfoBlock>
                    <StatusChip status={selectedPeriod.status} />
                  </div>
                  <Button
                    label="Export"
                    onClick={async () => {
                      const result = await fetchExportFilename({
                        period: selectedPeriod.date,
                        status: selectedPeriod.status,
                      });
                      if (result?.filename) {
                        window.open(`/download#filename=${result.filename}`, '_blank');
                      }
                    }}
                  />
                </div>
                <div className={styles.middleSection}>
                  <SearchInput
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleChangeSearch}
                    aria-label="RevShare Pane Search"
                  />
                  <div className={styles.middleSectionTotal}>
                    <span className={styles.middleSectionTotalLabel}>Total: </span>
                    <span aria-label="Period total">{selectedPeriod.total().toString()}</span>
                  </div>
                </div>
                {revenueShares.length > 0 ? (
                  <div className={styles.table}>
                    <Table data={revenueShares} columns={columns} />
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <StarLine />
                    <p>No Results Found</p>
                  </div>
                )}
              </SplitView.Content>
            </SplitView>
          )}
        </div>
      </Page.Content>
    </Page>
  );
}

export default RevenueShareView;
